// src/lib/session.ts
// Manejo unificado de sesión y compatibilidad con helpers legacy.

import { normalizeRole } from '@/auth/permissions';

export type SessionUser = {
  id: string;
  email: string;
  role: string;
  name: string;
  almacen_id?: number | null;
  isInternal?: boolean;
};

export type SessionData = {
  token: string | null;
  user: SessionUser | null;
};
export type Session = SessionData;
export type AuthNotice = {
  title: string;
  message: string;
  tone?: 'info' | 'warning';
};
export type AuthRedirectReason =
  | 'session_expired'
  | 'session_revoked'
  | 'session_required'
  | 'signed_out';

const JWT_KEY = 'jwt';
const USER_KEY = 'session_user';
const LEGACY_SESSION_KEY = 'app_session';
const LEGACY_TOKEN_KEY = 'token';
const AUTH_NOTICE_KEY = 'auth.notice';
const AUTH_RETURN_TO_KEY = 'auth.returnTo';

type JwtSessionPayload = {
  id?: string | number | null;
  email?: string | null;
  role?: string | null;
  name?: string | null;
  almacen_id?: number | null;
  isInternal?: boolean | null;
  is_internal?: boolean | null;
};

const PROVIDER_EMAIL_BRANDS: Record<string, string> = {
  'aktech@sien.com': 'AKTECH',
  'argos@sien.com': 'ARGOS',
  'eaton@sien.com': 'EATON',
  'finder@sien.com': 'FINDER',
  'hoffman@sien.com': 'HOFFMAN',
  'ide@sien.com': 'IDE',
  'rittal@sien.com': 'RITTAL',
  'weidmuller@sien.com': 'WEIDMULLER',
};

function providerBrandFromEmail(email?: string | null): string | null {
  const normalized = (email ?? '').trim().toLowerCase();
  return normalized ? PROVIDER_EMAIL_BRANDS[normalized] ?? null : null;
}

function decodeJwtPayload(token: string | null): JwtSessionPayload | null {
  if (!token) return null;
  const [, payload] = token.split('.');
  if (!payload) return null;
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const decoded = window.atob(padded);
    return JSON.parse(decoded) as JwtSessionPayload;
  } catch {
    return null;
  }
}

function writeSessionUser(user: SessionUser): void {
  window.localStorage.setItem(USER_KEY, JSON.stringify(user));
}

function mergeUserWithToken(stored: SessionUser | null, token: string | null): SessionUser | null {
  if (typeof window === 'undefined') return stored;
  const payload = decodeJwtPayload(token);
  if (!payload) return stored;

  const email = payload.email ?? stored?.email ?? '';
  const id = payload.id ?? stored?.id ?? email;
  if (!email || !id) return stored;

  const providerBrand = providerBrandFromEmail(email);
  const role = providerBrand ? 'PROVEEDOR' : normalizeRole(payload.role ?? stored?.role ?? null);
  const next: SessionUser = {
    id: String(id),
    email,
    role,
    name: providerBrand ?? payload.name ?? stored?.name ?? email,
    almacen_id: payload.almacen_id ?? stored?.almacen_id ?? null,
    isInternal: payload.isInternal ?? payload.is_internal ?? stored?.isInternal ?? true,
  };

  if (
    !stored ||
    stored.id !== next.id ||
    stored.email !== next.email ||
    stored.role !== next.role ||
    stored.name !== next.name ||
    stored.almacen_id !== next.almacen_id ||
    stored.isInternal !== next.isInternal
  ) {
    try {
      writeSessionUser(next);
    } catch {
      return next;
    }
  }

  return next;
}

export function setSession(jwt: string | null, user?: SessionUser | null) {
  if (typeof window === 'undefined') return;
  try {
    if (!jwt) {
      window.localStorage.removeItem(JWT_KEY);
      window.localStorage.removeItem(USER_KEY);
      return;
    }

    window.localStorage.setItem(JWT_KEY, jwt);

    if (user) {
      writeSessionUser(user);
    }
  } catch {
    return;
  }
}

export function getJwt(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(JWT_KEY) || null;
  } catch {
    return null;
  }
}

export function getSessionUser(): SessionUser | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(USER_KEY);
    const token = getJwt();
    if (!raw) return mergeUserWithToken(null, token);
    return mergeUserWithToken(JSON.parse(raw) as SessionUser, token);
  } catch {
    return null;
  }
}

export function getSession(): SessionData {
  return { token: getJwt(), user: getSessionUser() };
}

export function clearSession() {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(JWT_KEY);
    window.localStorage.removeItem(USER_KEY);
    window.localStorage.removeItem(LEGACY_SESSION_KEY);
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch {
    return;
  }
}

// Legacy helpers kept for existing callers.
export function getToken(): string | null {
  return getJwt();
}

export function setToken(token: string | null) {
  setSession(token ?? null);
}

export function clearToken() {
  clearSession();
}

export function getUser(): SessionUser | null {
  return getSessionUser();
}

export function isAdmin(): boolean {
  const user = getUser();
  if (!user) return false;
  const role = normalizeRole(user.role);
  return ['ADMIN', 'GERENTE_INV', 'SUPERVISOR'].includes(role);
}

export function setAuthNotice(notice: AuthNotice | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (!notice) {
      window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
      return;
    }
    window.sessionStorage.setItem(AUTH_NOTICE_KEY, JSON.stringify(notice));
  } catch {
    return;
  }
}

export function consumeAuthNotice(): AuthNotice | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(AUTH_NOTICE_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(AUTH_NOTICE_KEY);
    const parsed = JSON.parse(raw) as AuthNotice;
    if (!parsed?.title || !parsed?.message) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function rememberAuthReturnTo(path: string | null | undefined): void {
  if (typeof window === 'undefined') return;
  const normalized = typeof path === 'string' ? path.trim() : '';
  if (!normalized || normalized === '/login' || normalized.startsWith('/login?')) return;
  try {
    window.sessionStorage.setItem(AUTH_RETURN_TO_KEY, normalized);
  } catch {
    return;
  }
}

export function consumeAuthReturnTo(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(AUTH_RETURN_TO_KEY);
    if (!raw) return null;
    window.sessionStorage.removeItem(AUTH_RETURN_TO_KEY);
    if (!raw.startsWith('/') || raw.startsWith('//') || raw.startsWith('/login')) return null;
    return raw;
  } catch {
    return null;
  }
}

function noticeForReason(reason: AuthRedirectReason | undefined): AuthNotice | null {
  switch (reason) {
    case 'session_revoked':
      return {
        title: 'Tu sesión se cerró',
        message: 'Esta cuenta inició sesión en otro dispositivo. Inicia sesión de nuevo para continuar.',
        tone: 'warning',
      };
    case 'session_required':
      return {
        title: 'Inicia sesión para continuar',
        message: 'Tu sesión ya no está activa. Vuelve a entrar para seguir usando el sistema.',
        tone: 'info',
      };
    case 'session_expired':
      return {
        title: 'Vuelve a iniciar sesión',
        message: 'Tu sesión terminó por seguridad. Inicia sesión de nuevo para continuar.',
        tone: 'warning',
      };
    default:
      return null;
  }
}

export function redirectToLogin(options?: {
  reason?: AuthRedirectReason;
  returnTo?: string | null;
  rememberCurrentPath?: boolean;
  showNotice?: boolean;
}): void {
  clearSession();
  if (typeof window === 'undefined') return;

  const {
    reason = 'session_expired',
    returnTo = null,
    rememberCurrentPath = true,
    showNotice = true,
  } = options ?? {};

  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (rememberCurrentPath) {
    rememberAuthReturnTo(returnTo ?? currentPath);
  }
  if (showNotice) {
    setAuthNotice(noticeForReason(reason));
  } else {
    setAuthNotice(null);
  }

  if (!/\/login\b/.test(window.location.pathname)) {
    window.location.replace('/login');
  }
}
